/**
 * HELM's This week view (A7Y-4984, one of HELM's views since A7Y-5210): `/admin/cmo/crm/this-week`.
 *
 * A7Y-5342: the week leads — today, the rest of the week, next week — and the overdue
 * list sits below it, folded behind its count (77 items opened the page on 14 Oct). A
 * week with no plan shows last week's, labelled as last week's.
 *
 * Replaces reading `helm-roster.md` top to bottom. Every open roster item —
 * touches, follow-ups owed and scheduled decisions — grouped as overdue, each
 * remaining day of this week, and next week (`groupThisWeek`), beside the
 * current week plan (`weekPlans/{Monday}`). Each item names its account
 * (linked) and block, and is re-dated or closed in place with the account
 * page's own controls; an item with no account offers a control to link one.
 * A row whose HELM proposal Inbox lists under Refused is left out, and counted,
 * until HELM proposes it again — as on HELM's Today (A7Y-5873).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CrmAccountListItem, CrmRosterItemDoc, CrmWeekPlanDoc } from '@a7y/types';
import { PageHeader } from '@/components/PageHeader';
import { HelmViewNav } from '../HelmViews';
import { useAdminAuth } from '@/contexts/useAdminAuth';
import { api } from '@/lib/api';
import { addDays, groupThisWeek, isoDate } from './accountView';
import { shortDate } from './board';
import { AccountNameLink, InlineMarkdown } from './CrmMarkdown';
import { RosterLine } from './CrmAccountPage';
import { LinkItemEditor, RosterItemActions, Toggle, WeekPlanEditor } from './CrmEditors';
import { SlotsPanel } from './SlotsPanel';
import { crmErrorMessage, isCrmNotFoundError, isCrmUnavailableError } from './crmErrors';
import { resolveAccount } from './scoreboard';
import { HELM_OVERVIEW_QUERY_KEY } from '../helm/useHelmActions';
import { refusedRosterIds } from '../helm/staleRows';

function crmRetry(err: unknown, failureCount: number): boolean {
  return !isCrmUnavailableError(err) && !isCrmNotFoundError(err) && failureCount < 1;
}

export function CrmWeekPage({ today = isoDate(new Date()) }: { today?: string }) {
  const grouped = useQuery({
    queryKey: ['crm', 'roster-items', 'open'],
    queryFn: () => api.getCrmRosterItems('open'),
    retry: crmRetry,
  });
  const accounts = useQuery({ queryKey: ['crm', 'accounts'], queryFn: api.getCrmAccounts, retry: crmRetry });
  const weekStart = groupThisWeek([], today).weekStart;
  const plan = useQuery({
    queryKey: ['crm', 'week-plan', weekStart],
    queryFn: () => api.getCrmWeekPlan(weekStart),
    retry: crmRetry,
  });
  // A7Y-5342: with no plan this week, last week's is shown, labelled; its read never holds up the page.
  const previousWeekStart = addDays(weekStart, -7);
  const previousPlan = useQuery({
    queryKey: ['crm', 'week-plan', previousWeekStart],
    queryFn: () => api.getCrmWeekPlan(previousWeekStart),
    enabled: isCrmNotFoundError(plan.error),
    retry: crmRetry,
  });
  // A7Y-5873: a row whose HELM proposal Inbox lists under Refused is not due until HELM
  // proposes it again — on This week as on Today. HELM's own read, the one Today
  // makes; it never holds up the week, and unread it hides nothing.
  const helm = useQuery({ queryKey: HELM_OVERVIEW_QUERY_KEY, queryFn: api.getHelmOverview, retry: false });
  const refused = refusedRosterIds(helm.data?.current?.facts.proposals ?? []);
  // A7Y-5210: slots and parked are edited here; their read never holds up the week.
  const slots = useQuery({ queryKey: ['crm', 'slots'], queryFn: api.getCrmSlots, retry: crmRetry });

  const queries = [grouped, accounts];
  // A week with no plan answers 404: that is "no plan yet", not a failure.
  const planError = isCrmNotFoundError(plan.error) ? null : plan.error;
  const failure = [...queries.map((q) => q.error), planError].find((e) => e != null);

  let body: React.ReactNode;
  if ([...queries, plan].some((q) => isCrmUnavailableError(q.error))) {
    body = (
      <p data-testid="crm-unavailable" className="px-2 text-sm text-zinc-600 dark:text-zinc-400">
        HELM&rsquo;s record is available on production only.
      </p>
    );
  } else if (failure != null) {
    body = (
      <p role="alert" data-testid="crm-error" className="px-2 text-sm text-red-600 dark:text-red-400">
        Could not load the week: {crmErrorMessage(failure)}
      </p>
    );
  } else if (queries.some((q) => q.data === undefined) || (plan.data === undefined && plan.error == null)) {
    body = (
      <p data-testid="crm-loading" className="px-2 text-sm text-zinc-500">
        Loading…
      </p>
    );
  } else if (!grouped.data!.imported || !accounts.data!.imported) {
    body = (
      <p data-testid="crm-not-imported" className="px-2 text-sm text-zinc-600 dark:text-zinc-400">
        HELM&rsquo;s record has not been imported yet
      </p>
    );
  } else {
    body = (
      <>
        <WeekView
          items={grouped.data!.items.filter((i) => !refused.has(i.id))}
          refusedCount={grouped.data!.items.filter((i) => refused.has(i.id)).length}
          accounts={accounts.data!.accounts}
          plan={plan.data?.weekPlan ?? null}
          previousPlan={previousPlan.data?.weekPlan ?? null}
          today={today}
        />
        <div className="mt-8">
          {slots.data?.imported ? (
            <SlotsPanel
              slots={slots.data.slots}
              parked={slots.data.parked}
              accounts={accounts.data!.accounts}
              editable
            />
          ) : slots.error != null ? (
            <p role="alert" data-testid="crm-slots-error" className="px-2 text-sm text-red-600 dark:text-red-400">
              Could not load the slots: {crmErrorMessage(slots.error)}
            </p>
          ) : null}
        </div>
      </>
    );
  }

  return (
    <div className="pb-12">
      <HelmViewNav />
      <PageHeader title="This week" subtitle="Roster items and scheduled decisions due, and the week plan" />
      {body}
    </div>
  );
}

function WeekView({
  items,
  refusedCount,
  accounts,
  plan,
  previousPlan,
  today,
}: {
  items: CrmRosterItemDoc[];
  /** Open rows left out because HELM's proposal on them is under Refused (A7Y-5873). */
  refusedCount: number;
  accounts: CrmAccountListItem[];
  plan: CrmWeekPlanDoc | null;
  previousPlan: CrmWeekPlanDoc | null;
  today: string;
}) {
  const { isSuperAdmin } = useAdminAuth();
  const [showOverdue, setShowOverdue] = useState(false);
  const week = groupThisWeek(items, today);
  // The week leads; overdue follows it, folded (A7Y-5342).
  const overdue = week.groups.find((g) => g.id === 'overdue') ?? null;
  const ahead = week.groups.filter((g) => g.id !== 'overdue');
  return (
    <div className="grid gap-8 px-2 lg:grid-cols-[2fr_1fr]">
      {/* A7Y-5251: min-w-0 and overflow-wrap keep long URLs and paths in record text from widening the page at 375 px. */}
      <div className="min-w-0 space-y-6 [overflow-wrap:anywhere]">
        {refusedCount > 0 ? (
          <p data-testid="crm-week-refused" className="text-sm text-zinc-500">
            {refusedCount} {refusedCount === 1 ? 'row is' : 'rows are'} not listed: HELM&rsquo;s proposal on{' '}
            {refusedCount === 1 ? 'it' : 'them'} is under Refused on HELM&rsquo;s Inbox tab.
          </p>
        ) : null}
        {ahead.map((group) => (
          <section key={group.id} aria-label={group.label} data-testid={`crm-week-group-${group.id}`}>
            <h2 className="mb-2 text-sm font-semibold text-zinc-900 dark:text-white">
              {group.label}
              <span className="ml-2 text-xs font-normal text-zinc-500">{group.items.length}</span>
            </h2>
            {group.items.length === 0 ? (
              <p className="text-sm text-zinc-500">Nothing due.</p>
            ) : (
              <ul className="divide-y divide-zinc-100 text-sm dark:divide-zinc-800">
                {group.items.map((item) => (
                  <WeekItem key={item.id} item={item} accounts={accounts} isSuperAdmin={isSuperAdmin} />
                ))}
              </ul>
            )}
          </section>
        ))}
        {week.later > 0 ? (
          <p data-testid="crm-week-later" className="text-xs text-zinc-500">
            {week.later} more open {week.later === 1 ? 'item is' : 'items are'} due after next week.
          </p>
        ) : null}
        {overdue && overdue.items.length === 0 ? (
          <section aria-label={overdue.label} data-testid="crm-week-group-overdue">
            <h2 className="mb-2 text-sm font-semibold text-zinc-900 dark:text-white">
              {overdue.label}
              <span className="ml-2 text-xs font-normal text-zinc-500">0</span>
            </h2>
            <p className="text-sm text-zinc-500">Nothing overdue.</p>
          </section>
        ) : null}
        {overdue && overdue.items.length > 0 ? (
          <section aria-label={overdue.label} data-testid="crm-week-group-overdue">
            <h2 className="mb-2 text-sm font-semibold text-red-700 dark:text-red-400">
              {overdue.label}
              <span className="ml-2 text-xs font-normal text-zinc-500">{overdue.items.length}</span>
            </h2>
            <button
              type="button"
              data-testid="crm-week-overdue-toggle"
              aria-expanded={showOverdue}
              onClick={() => setShowOverdue((v) => !v)}
              className="min-h-11 text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              {showOverdue ? 'Hide overdue' : `Show ${overdue.items.length} overdue`}
            </button>
            {showOverdue ? (
              <ul className="divide-y divide-zinc-100 text-sm dark:divide-zinc-800">
                {overdue.items.map((item) => (
                  <WeekItem key={item.id} item={item} accounts={accounts} isSuperAdmin={isSuperAdmin} />
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}
      </div>
      <WeekPlan plan={plan} previousPlan={previousPlan} weekStart={week.weekStart} accounts={accounts} today={today} />
    </div>
  );
}

function WeekItem({
  item,
  accounts,
  isSuperAdmin,
}: {
  item: CrmRosterItemDoc;
  accounts: CrmAccountListItem[];
  isSuperAdmin: boolean;
}) {
  const account = resolveAccount(item.accountId, accounts);
  return (
    <li data-testid="crm-week-item" className="space-y-1 py-2">
      <div>
        <span className="mr-2 tabular-nums text-zinc-500">{item.due}</span>
        {account.accountId === null ? (
          <span data-testid="crm-week-unlinked" className="mr-2 text-amber-700 dark:text-amber-400">
            No account
          </span>
        ) : (
          <span className="mr-2">
            {account.name !== null ? (
              <AccountNameLink accountId={account.accountId} name={account.name} />
            ) : (
              <span data-testid="crm-week-unknown-account">
                <code className="text-xs">{account.accountId}</code>{' '}
                <span className="text-amber-700 dark:text-amber-400">(unknown account)</span>
              </span>
            )}
          </span>
        )}
        <RosterLine item={item} />
      </div>
      <div className="flex flex-wrap items-start gap-3">
        <RosterItemActions item={item} isSuperAdmin={isSuperAdmin} />
        {item.accountId === null ? (
          <Toggle label="Link to an account">
            {(close) => <LinkItemEditor item={item} accounts={accounts} onClose={close} />}
          </Toggle>
        ) : null}
      </div>
    </li>
  );
}

function PlanBody({
  plan,
  accounts,
  testId,
}: {
  plan: CrmWeekPlanDoc;
  accounts: CrmAccountListItem[];
  testId: string;
}) {
  return (
    <>
      {plan.note ? (
        <p className="whitespace-pre-wrap text-sm text-zinc-700 dark:text-zinc-300">
          <InlineMarkdown text={plan.note} />
        </p>
      ) : null}
      {plan.moves.length === 0 ? (
        <p className="text-sm text-zinc-500">No moves.</p>
      ) : (
        <ol className="space-y-2 text-sm text-zinc-900 dark:text-zinc-100">
          {plan.moves.map((move, i) => {
            const account = resolveAccount(move.accountId, accounts);
            return (
              <li key={i} data-testid={testId}>
                <span className="tabular-nums text-zinc-500">{i + 1}.</span> <InlineMarkdown text={move.move} />
                <span className="block text-xs text-zinc-500">
                  {account.accountId !== null && account.name !== null ? (
                    <AccountNameLink
                      accountId={account.accountId}
                      name={account.name}
                      className="text-blue-600 hover:underline dark:text-blue-400"
                    />
                  ) : null}
                  {account.accountId !== null && account.name === null ? (
                    <span>
                      <code>{account.accountId}</code>{' '}
                      <span className="text-amber-700 dark:text-amber-400">(unknown account)</span>
                    </span>
                  ) : null}
                  {account.accountId !== null ? ' · ' : ''}
                  {move.owner} · {move.day}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </>
  );
}

function WeekPlan({
  plan,
  previousPlan,
  weekStart,
  accounts,
  today,
}: {
  plan: CrmWeekPlanDoc | null;
  previousPlan: CrmWeekPlanDoc | null;
  weekStart: string;
  accounts: CrmAccountListItem[];
  today: string;
}) {
  return (
    <aside aria-label="Week plan" data-testid="crm-week-plan" className="min-w-0 space-y-2 [overflow-wrap:anywhere]">
      <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">Week plan · {weekStart}</h2>
      <Toggle label={plan === null ? 'Write the plan' : 'Edit the plan'}>
        {(close) => <WeekPlanEditor weekStart={weekStart} plan={plan} accounts={accounts} onClose={close} />}
      </Toggle>
      {plan === null ? (
        <>
          <p data-testid="crm-week-plan-none" className="text-sm text-zinc-500">
            No plan has been set for this week.
          </p>
          {previousPlan ? (
            <div data-testid="crm-week-plan-previous" className="space-y-2 opacity-80">
              <p className="text-xs font-medium text-zinc-600 dark:text-zinc-400">
                Last week&rsquo;s plan, week of {shortDate(previousPlan.weekStart, today)}
              </p>
              <PlanBody plan={previousPlan} accounts={accounts} testId="crm-week-previous-move" />
            </div>
          ) : null}
        </>
      ) : (
        <PlanBody plan={plan} accounts={accounts} testId="crm-week-move" />
      )}
    </aside>
  );
}
